import { CART_JOURNEY_CTX_KEYS, NativeLocalCart, ReviewedCartApiResponse } from "@swiggy-private/cart-types";

import { getOnPremiseCartJourneyAdditionalContext } from "@cart-client/helpers/OnPremiseFulfilment";

export type CartJourneyContextParams = NonNullable<NativeLocalCart["cart_journey_ctx"]>;

/**
 * Removes specified keys from additional_context object.
 * Used when backend signals context should be dropped via cart_context_drop.
 *
 * IMPORTANT: When all keys are dropped, returns undefined (not empty object).
 * This ensures additional_context is completely removed from cart_journey_ctx,
 * which gets synced to Apps via bridge. Once dropped, context is gone from both Portal and Apps.
 */
function cleanAdditionalContext(
    additionalContext?: { [key: string]: string },
    keysToDrop?: string[],
): { [key: string]: string } | undefined {
    if (!additionalContext || !keysToDrop || keysToDrop.length === 0) {
        return additionalContext;
    }

    const cleanedContext: { [key: string]: string } = {};
    let hasRemainingKeys = false;

    for (const key in additionalContext) {
        if (!keysToDrop.includes(key)) {
            cleanedContext[key] = additionalContext[key];
            hasRemainingKeys = true;
        }
    }

    return hasRemainingKeys ? cleanedContext : undefined;
}

function buildAdditionalContext(
    cartJourneyCtx?: CartJourneyContextParams,
    cartData?: ReviewedCartApiResponse,
): { [key: string]: string } | undefined {
    const mergedContext = {
        ...(cartJourneyCtx?.additional_context ?? {}),
        ...getOnPremiseCartJourneyAdditionalContext(cartData),
    };

    return cleanAdditionalContext(mergedContext, cartData?.cart_context_drop ?? []);
}

export function buildCartJourneyContext(
    cartJourneyCtx?: CartJourneyContextParams,
    cartData?: ReviewedCartApiResponse,
): CartJourneyContextParams {
    const additionalContext = buildAdditionalContext(cartJourneyCtx, cartData);

    return {
        [CART_JOURNEY_CTX_KEYS.CART_INITIALIZATION_SOURCE]: cartJourneyCtx?.cart_initialization_source,
        [CART_JOURNEY_CTX_KEYS.CART_CATEGORY_TYPE]: cartJourneyCtx?.cart_category_type,
        [CART_JOURNEY_CTX_KEYS.REQUEST_CONTEXTS]: cartJourneyCtx?.request_contexts,
        ...(additionalContext &&
            Object.keys(additionalContext).length > 0 && {
                [CART_JOURNEY_CTX_KEYS.ADDITIONAL_CONTEXT]: additionalContext,
            }),
    };
}
